import { useCallback, useState } from "react";
import styled from "styled-components";
import Button from "./Common/Button";
import { usePlayersContext, useSocketContext } from "./Common/Contexts";
import { EditableFieldWithConfirmation } from "./EditableField";
import { useTranslationContext } from "../Hooks/useTranslationContext";
import PlayerName from "./PlayerName";
import Game from "../Types/Game";
import RandomIcon from "../Icons/RandomIcon";
import { MediumHeading } from "./Common/Typography";
import GameSettings from "./GameSettings";
import SettingsIcon from "Icons/SettingsIcon";
import { getWinConditionString } from "Helpers/util";

interface LobbyProps {
  gameId: number;
  game: Game;
}

//copy-pasted from https://blog.logrocket.com/drag-and-drop-in-react/
//simple way to check whether the device support touch (it doesn't check all fallback, it supports only modern browsers)
const isTouchDevice = () => {
  if ("ontouchstart" in window) {
    return true;
  }
  return false;
};

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  grid-template-rows: auto;
  grid-template-areas:
    "header header header header header header header header header"
    "players players players categories categories categories options options options"
    ". . .  settings winCondition start . . .";
  @media screen and (max-width: 80em) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      "header header header header header"
      "players categories categories categories ."
      "options categories categories categories ."
      ".  settings winCondition start .";
  }
  @media screen and (max-width: 50em) {
    display: inherit;
  }
`;

const LargeHeading = styled(MediumHeading)`
  font-size: 1.3em;
  grid-column: span 3;
  grid-area: header;
`;
const PlayersList = styled.div`
  padding: 1em 0px 1em 0px;
  grid-area: players;
`;

const Categories = styled.div`
  background: var(--color-bg-highlight);
  border: solid var(--color-text-light);
  border-radius: 0.8em;
  grid-area: categories;
`;
const StyledButton = styled(Button)`
  margin-bottom: 2em;
`;
const RightPaddingButton = styled(Button)`
  padding-right: 1em;
  display: flex;
  align-items: center;
`;
const StyledDeleteButton = styled(Button)`
  margin-left: 0.5em;
`;
const MiddleColumn = styled.div`
  margin-top: 1em;
  grid-area: start;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
`;
const CategoryButtons = styled.div`
  position: relative;
`;
const WinCondition = styled.div`
  margin-top: 2em;
  grid-area: winCondition;
`;
const SettingsButtonContainer = styled.div`
  margin-top: 1em;
  grid-area: settings;
  display: flex;
  justify-self: center;
  justify-content: center;
  align-self: center;
`;

const EditableFieldContainer = styled.div`
  display: flex;
  align-items: center;
  ${!isTouchDevice() &&
  `
    #delete-button {
      visibility: hidden;
    }
  `}

  :hover {
    #delete-button {
      visibility: visible;
    }
  }
`;

const Lobby = ({ game, gameId }: LobbyProps) => {
  const socket = useSocketContext();
  const { players } = usePlayersContext();
  const { language } = useTranslationContext();
  const [showSettings, setShowSettings] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [
    showTimeLimitInputErrorMessage,
    setShowTimeLimitInputErrorMessage,
  ] = useState(false);
  const { t } = useTranslationContext();
  const handleStartGame = () => game && socket.emit("startGame", gameId);
  const handleOnAddCategory = () => {
    handleOnCategoryDone(undefined, newCategory);
    setNewCategory("");
  };
  const handleOnRandomize = () =>
    socket.emit("randomizeCategories", { gameId, language });
  const handleOnCategoryDone = (index: number | undefined, newValue: string) =>
    socket.emit("categoryDone", { gameId, index, newValue });
  const handleOnCategoryChange = useCallback(
    (index: number | undefined, isEditing: boolean) =>
      socket.emit("categoryChange", {
        gameId,
        socketId: isEditing ? socket.id : undefined,
        index,
      }),
    [gameId, socket]
  );
  const handleOnCategoryDelete = (index: number | undefined) =>
    socket.emit("categoryDelete", { gameId, index });
  const onToggleShowSettings = useCallback(() => setShowSettings((s) => !s), [
    setShowSettings,
  ]);
  const { getSocketName } = usePlayersContext();
  return (
    <>
      <Container>
        <LargeHeading>{`${t["welcome.to.the.lobby.for.game"]} ${gameId}`}</LargeHeading>
        <PlayersList>
          <MediumHeading>{`${t.players}:`}</MediumHeading>
          <FlexContainer>
            {game.socketIds.map((s) => (
              <PlayerName
                key={s}
                name={getSocketName(s)}
                isConnected={players[s].isConnected}
                showIcons
              />
            ))}
          </FlexContainer>
        </PlayersList>
        <Categories>
          <MediumHeading>{`${t.categories}:`}</MediumHeading>
          {game.categories.map((c, i) => (
            <EditableFieldContainer key={i}>
              <StyledDeleteButton
                id="delete-button"
                isSmall
                isSecondary
                onClick={() => handleOnCategoryDelete(i)}
              >
                {t.delete}
              </StyledDeleteButton>
              <EditableFieldWithConfirmation
                type="text"
                placeholder={t["write.a.category"]}
                category={c.name}
                editingSocketName={
                  !c.editingSocketId || c.editingSocketId === socket.id
                    ? undefined
                    : getSocketName(c.editingSocketId)
                }
                index={i}
                handleOnCategoryDone={handleOnCategoryDone}
                handleOnCategoryChange={handleOnCategoryChange}
              />
            </EditableFieldContainer>
          ))}
          <CategoryButtons>
            <StyledButton isSmall onClick={handleOnAddCategory}>
              {t["add.another.category"]}
            </StyledButton>
            <RandomIcon onClick={handleOnRandomize} />
          </CategoryButtons>
        </Categories>
        <SettingsButtonContainer>
          <RightPaddingButton
            isSmall
            isSecondary
            onClick={() => setShowSettings((s) => !s)}
          >
            <SettingsIcon />
            {`${t.options}`}
          </RightPaddingButton>
        </SettingsButtonContainer>
        {showSettings && (
          <GameSettings
            gameId={gameId}
            game={game}
            showTimeLimitInputErrorMessage={showTimeLimitInputErrorMessage}
            setShowTimeLimitInputErrorMessage={
              setShowTimeLimitInputErrorMessage
            }
            onOverlayClick={onToggleShowSettings}
            onExit={onToggleShowSettings}
          />
        )}
        <WinCondition>
          {getWinConditionString(
            t,
            game.winCondition,
            game.winConditionPoints,
            game.winConditionRounds
          )}
        </WinCondition>
        <MiddleColumn>
          <Button
            onClick={handleStartGame}
            disabled={game.timeLimitEnabled && showTimeLimitInputErrorMessage}
          >
            {t.start}
          </Button>
        </MiddleColumn>
      </Container>
    </>
  );
};

export default Lobby;
